import type {ChatState} from '@microsoft/agent-host-protocol'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clsx} from 'clsx'
import {useCallback, useMemo, useState, type PropsWithChildren, type ReactNode, type RefObject} from 'react'
import {createPortal} from 'react-dom'
import {Dialog} from '@primer/react'

import {SavedTaskChangesetContext, useTaskChangeset} from '../../hooks/use-task-changeset'
import type {Artifact} from '../../utils/artifacts/artifact-core'
import {changesetFilesToDiffData} from '../../utils/changeset-to-diff-data'
import {useAnnouncedSessionArtifacts, useArtifactsAction, type ArtifactsAction} from '../artifacts/SessionArtifacts'
import {useAutoOpenSessionArtifact} from '../artifacts/use-auto-open-session-artifact'
import {AhpTaskChatInputActionBar} from './AhpTaskChatInputActionBar/AhpTaskChatInputActionBar'
import {DiffTriggerButton} from './AhpTaskChatInputActionBar/DiffTriggerButton'
import styles from './AhpTaskChatInputActionBar/AhpTaskChatInputActionBar.module.css'
import {
  useBackgroundTasksAction,
  useEstablishedBackgroundTasks,
  usePersistedBackgroundTasks,
  type BackgroundTask,
} from './BackgroundTasks'
import type {TaskHistorySession} from '../../utils/replay-task-history-events'
import {AhpTaskChatInputActions} from './AhpTaskChatInputActions'
import {DiffOverlay} from './DiffOverlay'

function ChangesetDialogBody({children}: PropsWithChildren) {
  return <div className={styles.ChangesetDialogBody}>{children}</div>
}

/** Chat uses the same replay selection and live subscription as Task Chat. */
export function ChatChangesetActions({taskId, saved}: {taskId: string; saved?: TaskHistorySession}) {
  const [isOpen, setIsOpen] = useState(false)
  if (!isFeatureEnabled('copilot_mission_control_ahp_diff')) return null
  return (
    <SavedTaskChangesetContext value={saved}>
      <AhpTaskChatInputActions repoNwo="" taskId={taskId} baseRef="" headRef="" onViewDiff={() => setIsOpen(true)} />
      {isOpen && (
        <Dialog
          title="Changed files"
          width="xlarge"
          height="large"
          onClose={() => setIsOpen(false)}
          renderBody={ChangesetDialogBody}
        >
          <DiffOverlay
            isOpen
            withinDialog
            onClose={() => setIsOpen(false)}
            repoNwo=""
            taskId={taskId}
            baseRef=""
            headRef=""
          />
        </Dialog>
      )}
    </SavedTaskChangesetContext>
  )
}

interface TaskInputActionsProps {
  artifacts: Artifact[]
  tasks: readonly BackgroundTask[]
  focusTargetRef?: RefObject<HTMLTextAreaElement | null>
  /**
   * Resolves the focus target when the artifacts panel unmounts, for surfaces
   * whose composer is not a textarea. Defaults to
   * {@link TaskInputActionsProps.focusTargetRef}.
   */
  getArtifactsFallbackFocus?: () => HTMLElement | null
  /**
   * Whether `artifacts` reflects the session's fully loaded history. Absence of
   * an artifact only counts as a removal once this is true.
   */
  historyIsSettled?: boolean
  artifactWritesSettled?: boolean
  onViewDiff?: () => void
  /**
   * When provided, the artifacts/background-tasks panel portals into this element instead of
   * rendering as a sibling of the trigger row inside this component's own wrapper — so opening
   * the panel never forces the trigger row (a flex item sharing a line with the Diff/PR row) to
   * claim the full row width and wrap onto its own line. Omit to keep the panel stacked above the
   * trigger inside this component's own wrapper.
   */
  panelPortalTarget?: HTMLElement | null
}

function TaskInputActionsLayout({
  changesTrigger,
  artifactsAction,
  backgroundTasksAction,
  panelPortalTarget,
}: {
  changesTrigger?: ReactNode
  artifactsAction: ArtifactsAction
  backgroundTasksAction?: ReturnType<typeof useBackgroundTasksAction>
  panelPortalTarget?: HTMLElement | null
}) {
  const hasOpenPanel = artifactsAction.isOpen || Boolean(backgroundTasksAction?.panel)
  const panels = (
    <div className={styles.EstablishedActionPanels} onFocus={artifactsAction.onSurfaceFocus}>
      {artifactsAction.panel}
      {backgroundTasksAction?.panel}
    </div>
  )
  return (
    <div
      className={clsx(
        styles.EstablishedActions,
        !panelPortalTarget && hasOpenPanel && styles.EstablishedActionsExpanded,
      )}
      data-testid="established-task-input-actions"
      onFocus={panelPortalTarget ? undefined : artifactsAction.onSurfaceFocus}
    >
      {panelPortalTarget ? createPortal(panels, panelPortalTarget) : panels}
      <AhpTaskChatInputActionBar>
        {changesTrigger}
        {artifactsAction.trigger}
        {backgroundTasksAction?.trigger}
      </AhpTaskChatInputActionBar>
    </div>
  )
}

function TaskInputActionsWithBackground({
  changesTrigger,
  artifactsAction,
  tasks,
  focusTargetRef,
  panelPortalTarget,
}: Pick<TaskInputActionsProps, 'tasks' | 'focusTargetRef' | 'panelPortalTarget'> & {
  changesTrigger?: ReactNode
  artifactsAction: ArtifactsAction
}) {
  const backgroundTasksAction = useBackgroundTasksAction({tasks, focusTargetRef})
  return (
    <TaskInputActionsLayout
      changesTrigger={changesTrigger}
      artifactsAction={artifactsAction}
      backgroundTasksAction={backgroundTasksAction}
      panelPortalTarget={panelPortalTarget}
    />
  )
}

export function TaskInputActions({
  artifacts,
  tasks,
  focusTargetRef,
  getArtifactsFallbackFocus,
  historyIsSettled,
  artifactWritesSettled,
  onViewDiff,
  panelPortalTarget,
}: TaskInputActionsProps) {
  const getFallbackFocusTarget = useCallback(
    () => getArtifactsFallbackFocus?.() ?? focusTargetRef?.current ?? null,
    [getArtifactsFallbackFocus, focusTargetRef],
  )
  const artifactsAction = useArtifactsAction(artifacts, getFallbackFocusTarget, historyIsSettled, artifactWritesSettled)
  const changeset = useTaskChangeset(isFeatureEnabled('copilot_mission_control_ahp_diff'))
  const changesetDiffs = useMemo(() => changesetFilesToDiffData(changeset.files), [changeset.files])
  const changesTrigger =
    changeset.available &&
    !changeset.persisted &&
    (changeset.status === 'ready' || changeset.status === 'recomputing') &&
    changeset.files.length > 0 ? (
      <DiffTriggerButton
        additions={changesetDiffs.additions}
        deletions={changesetDiffs.deletions}
        onViewDiff={onViewDiff}
      />
    ) : undefined

  if (tasks.length > 0) {
    return (
      <TaskInputActionsWithBackground
        changesTrigger={changesTrigger}
        artifactsAction={artifactsAction}
        tasks={tasks}
        focusTargetRef={focusTargetRef}
        panelPortalTarget={panelPortalTarget}
      />
    )
  }
  if (artifacts.length === 0 && !changesTrigger) return null
  return (
    <TaskInputActionsLayout
      changesTrigger={changesTrigger}
      artifactsAction={artifactsAction}
      panelPortalTarget={panelPortalTarget}
    />
  )
}

interface EstablishedTaskChatInputActionsProps {
  taskId: string
  sessionId: string
  persistedState?: ChatState
  liveState?: ChatState
  focusTargetRef?: RefObject<HTMLTextAreaElement | null>
  getArtifactsFallbackFocus?: () => HTMLElement | null
  onRefetchPersistedHistory?: () => void
  onViewDiff?: () => void
  panelPortalTarget?: HTMLElement | null
}

type PersistedTaskChatInputActionsProps = Omit<EstablishedTaskChatInputActionsProps, 'liveState'>

interface EstablishedTaskInputActionsProps extends Omit<EstablishedTaskChatInputActionsProps, 'taskId'> {
  artifacts: Artifact[]
  historyIsSettled?: boolean
  artifactWritesSettled?: boolean
}

export function EstablishedTaskInputActions({
  artifacts,
  sessionId,
  persistedState,
  focusTargetRef,
  getArtifactsFallbackFocus,
  historyIsSettled,
  artifactWritesSettled,
  onViewDiff,
  panelPortalTarget,
}: EstablishedTaskInputActionsProps) {
  const tasks = useEstablishedBackgroundTasks(sessionId, persistedState)
  return (
    <TaskInputActions
      artifacts={artifacts}
      tasks={tasks}
      focusTargetRef={focusTargetRef}
      getArtifactsFallbackFocus={getArtifactsFallbackFocus}
      historyIsSettled={historyIsSettled}
      artifactWritesSettled={artifactWritesSettled}
      onViewDiff={onViewDiff}
      panelPortalTarget={panelPortalTarget}
    />
  )
}

export function PersistedTaskInputActions({
  artifacts,
  sessionId,
  persistedState,
  focusTargetRef,
  getArtifactsFallbackFocus,
  historyIsSettled,
  artifactWritesSettled,
  panelPortalTarget,
}: EstablishedTaskInputActionsProps) {
  const tasks = usePersistedBackgroundTasks(sessionId, persistedState)
  return (
    <TaskInputActions
      artifacts={artifacts}
      tasks={tasks}
      focusTargetRef={focusTargetRef}
      getArtifactsFallbackFocus={getArtifactsFallbackFocus}
      historyIsSettled={historyIsSettled}
      artifactWritesSettled={artifactWritesSettled}
      panelPortalTarget={panelPortalTarget}
    />
  )
}

export function PersistedTaskChatInputActions({
  taskId,
  sessionId,
  persistedState,
  focusTargetRef,
  getArtifactsFallbackFocus,
  onRefetchPersistedHistory,
  panelPortalTarget,
}: PersistedTaskChatInputActionsProps) {
  const {artifacts, historyIsSettled, artifactWritesSettled} = useAnnouncedSessionArtifacts(
    undefined,
    onRefetchPersistedHistory ? {chatState: persistedState, refetch: onRefetchPersistedHistory} : undefined,
  )
  useAutoOpenSessionArtifact(taskId, undefined, artifacts, historyIsSettled, artifactWritesSettled)
  return (
    <PersistedTaskInputActions
      artifacts={artifacts}
      sessionId={sessionId}
      persistedState={persistedState}
      focusTargetRef={focusTargetRef}
      getArtifactsFallbackFocus={getArtifactsFallbackFocus}
      historyIsSettled={historyIsSettled}
      artifactWritesSettled={artifactWritesSettled}
      panelPortalTarget={panelPortalTarget}
    />
  )
}

export function EstablishedTaskChatInputActions({
  taskId,
  sessionId,
  persistedState,
  liveState,
  focusTargetRef,
  getArtifactsFallbackFocus,
  onRefetchPersistedHistory,
  onViewDiff,
  panelPortalTarget,
}: EstablishedTaskChatInputActionsProps) {
  const {artifacts, historyIsSettled, artifactWritesSettled} = useAnnouncedSessionArtifacts(
    liveState,
    onRefetchPersistedHistory ? {chatState: persistedState, refetch: onRefetchPersistedHistory} : undefined,
  )
  useAutoOpenSessionArtifact(taskId, liveState, artifacts, historyIsSettled, artifactWritesSettled)
  return (
    <EstablishedTaskInputActions
      artifacts={artifacts}
      sessionId={sessionId}
      persistedState={persistedState}
      focusTargetRef={focusTargetRef}
      getArtifactsFallbackFocus={getArtifactsFallbackFocus}
      historyIsSettled={historyIsSettled}
      artifactWritesSettled={artifactWritesSettled}
      onViewDiff={onViewDiff}
      panelPortalTarget={panelPortalTarget}
    />
  )
}
